/**
 * Training Mode — Walkthrough Controller.
 *
 * Orchestrates step-through topics (spec §3). Reads the active topic's
 * `steps` from context, renders Spotlight for the current step, handles
 * Back/Next/Skip/Finish, route awareness (§4A), and persists progress
 * to /api/training/progress on each step change.
 *
 * Pause-at-my-action mode: when enabled via a toggle inside the floating
 * panel, clicking the spotlit element PAUSES the walkthrough (resume
 * button appears in the bubble / panel). Otherwise the click auto-advances
 * with a toast acknowledgment.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTraining } from "@/lib/trainingContext";
import { patchProgress } from "@/lib/trainingApi";
import Spotlight from "@/components/training/Spotlight";

// Session-scoped walkthrough position keyed by topic slug.
const SS_KEY_STEP = "training.mode.stepIndex";
const SS_KEY_PAUSED = "training.mode.paused";
const SS_KEY_SKIPS = "training.mode.skippedSteps";
const SS_KEY_PAUSE_MODE = "training.mode.pauseAtAction";

function readJSON(key, fallback) {
  try { const raw = sessionStorage.getItem(key); return raw ? JSON.parse(raw) : fallback; }
  catch { return fallback; }
}
function writeJSON(key, val) {
  try { sessionStorage.setItem(key, JSON.stringify(val)); } catch { /* noop */ }
}

export default function WalkthroughController() {
  const { enabled, activeTopic, setActiveTopic, overlayCount } = useTraining();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [stepIndex, setStepIndex] = useState(() => {
    const map = readJSON(SS_KEY_STEP, {});
    return (activeTopic?.slug && map[activeTopic.slug]) || 0;
  });
  const [paused, setPaused] = useState(() => readJSON(SS_KEY_PAUSED, false));
  const [pauseAtAction, setPauseAtAction] = useState(() => readJSON(SS_KEY_PAUSE_MODE, false));
  const [completed, setCompleted] = useState(false);
  const completedToastRef = useRef(false);

  // Reset step when topic changes
  useEffect(() => {
    if (!activeTopic?.slug) return;
    const map = readJSON(SS_KEY_STEP, {});
    const idx = map[activeTopic.slug] || 0;
    setStepIndex(idx);
    setCompleted(false);
    completedToastRef.current = false;
  }, [activeTopic?.slug]);

  const steps = useMemo(() => activeTopic?.steps || [], [activeTopic]);
  const currentStep = steps[stepIndex] || null;
  const total = steps.length;

  // Persist position locally + remotely
  const persist = useCallback((idx, { markComplete = false, markSkipped = false } = {}) => {
    if (!activeTopic?.slug) return;
    const map = readJSON(SS_KEY_STEP, {});
    map[activeTopic.slug] = idx;
    writeJSON(SS_KEY_STEP, map);

    // Build payload for PATCH /training/progress
    try {
      const inProg = readJSON("training.mode.inProgressCache", {});
      inProg[activeTopic.slug] = {
        topic_id: activeTopic.id,
        current_step: idx,
        total_steps: total,
        last_accessed: new Date().toISOString(),
      };
      writeJSON("training.mode.inProgressCache", inProg);
      const payload = { in_progress: inProg };
      if (markComplete) {
        const completedCache = readJSON("training.mode.completedCache", []);
        if (!completedCache.some((r) => r.slug === activeTopic.slug)) {
          completedCache.push({
            topic_id: activeTopic.id,
            slug: activeTopic.slug,
            completed_at: new Date().toISOString(),
          });
          writeJSON("training.mode.completedCache", completedCache);
        }
        delete inProg[activeTopic.slug];
        writeJSON("training.mode.inProgressCache", inProg);
        payload.in_progress = inProg;
        payload.completed_topics = completedCache;
      }
      if (markSkipped) {
        const skips = readJSON(SS_KEY_SKIPS, {});
        (skips[activeTopic.slug] = skips[activeTopic.slug] || []).push(idx);
        writeJSON(SS_KEY_SKIPS, skips);
        payload.skipped_steps = skips;
      }
      // fire-and-forget; network error must not break the walkthrough
      patchProgress(payload).catch(() => { /* noop */ });
    } catch {
      /* noop */
    }
  }, [activeTopic, total]);

  const advance = useCallback(() => {
    setPaused(false); writeJSON(SS_KEY_PAUSED, false);
    if (stepIndex + 1 >= total) {
      setCompleted(true);
      persist(total, { markComplete: true });
      if (!completedToastRef.current) {
        completedToastRef.current = true;
        toast.success(`🎉 Completed: ${activeTopic?.title || "topic"}`);
      }
      return;
    }
    const next = stepIndex + 1;
    setStepIndex(next);
    persist(next);
  }, [stepIndex, total, persist, activeTopic?.title]);

  const back = useCallback(() => {
    if (stepIndex === 0) return;
    setPaused(false); writeJSON(SS_KEY_PAUSED, false);
    const prev = stepIndex - 1;
    setStepIndex(prev);
    persist(prev);
  }, [stepIndex, persist]);

  const skip = useCallback(() => {
    if (stepIndex + 1 >= total) {
      setCompleted(true);
      persist(total, { markComplete: true, markSkipped: true });
      return;
    }
    const next = stepIndex + 1;
    setStepIndex(next);
    persist(next, { markSkipped: true });
  }, [stepIndex, total, persist]);

  const finish = useCallback(() => {
    setCompleted(true);
    persist(total, { markComplete: true });
    if (!completedToastRef.current) {
      completedToastRef.current = true;
      toast.success(`🎉 Completed: ${activeTopic?.title || "topic"}`);
    }
  }, [total, persist, activeTopic?.title]);

  // Route awareness (spec §4A) — auto-navigate to the expected route so the
  // user doesn't have to click a "Go to /leads →" prompt. We still keep the
  // manual fallback card visible for 600ms after triggering the navigation
  // in case the target route is slow to hydrate (React lazy-chunk, etc.).
  const expectedRoute = currentStep?.route || null;
  const routeMatches = !expectedRoute
    || pathname === expectedRoute
    || pathname.startsWith(expectedRoute.replace(/\/:.+$/, ""));

  const lastAutoNavRef = useRef({ route: null, ts: 0 });
  useEffect(() => {
    if (!expectedRoute || routeMatches || !enabled || !activeTopic || completed || paused) return;
    // Debounce so rapid re-renders don't loop navigate().
    const now = Date.now();
    if (lastAutoNavRef.current.route === expectedRoute && now - lastAutoNavRef.current.ts < 1500) return;
    lastAutoNavRef.current = { route: expectedRoute, ts: now };
    navigate(expectedRoute);
  }, [expectedRoute, routeMatches, enabled, activeTopic, completed, paused, navigate]);

  // Hide spotlight entirely when any overlay is open (picker modal, etc.)
  const suppressSpotlight = overlayCount > 0 || !enabled || !activeTopic || !currentStep || completed || paused;

  const handlePausedAction = useCallback(() => {
    setPaused(true); writeJSON(SS_KEY_PAUSED, true);
    toast.message("Paused — explore on your own. Click 'Resume' to continue.");
  }, []);

  const handleResume = useCallback(() => {
    setPaused(false); writeJSON(SS_KEY_PAUSED, false);
    advance();
  }, [advance]);

  const handleChangeTopic = useCallback(() => setActiveTopic(null), [setActiveTopic]);

  const togglePauseMode = useCallback(() => {
    setPauseAtAction((v) => { const nv = !v; writeJSON(SS_KEY_PAUSE_MODE, nv); return nv; });
  }, []);

  // Completion card replaces Spotlight on last step after Finish
  if (completed && activeTopic) {
    return (
      <div
        data-testid="training-completion-card"
        style={{
          position: "fixed",
          bottom: 20,
          right: 340, // sits next to the floating panel (which is 300 + 20 margin)
          width: 300,
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 10px 32px rgba(0,0,0,0.18)",
          border: "0.5px solid #ddd",
          padding: 16,
          zIndex: 2147483647,
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 500, color: "#111" }}>
          🎉 You completed: {activeTopic.title}
        </div>
        <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
          {total}-step walkthrough · ~{activeTopic.estimated_minutes || "?"} min
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 12 }}>
          <button
            type="button"
            data-testid="training-complete-practice"
            onClick={() => { setCompleted(false); setActiveTopic(null); toast.success("Enjoy — the trainer is here when you need it."); }}
            style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, padding: "7px 12px", fontSize: 12, fontWeight: 500, cursor: "pointer" }}
          >
            Practice on your own →
          </button>
          <button
            type="button"
            data-testid="training-complete-another"
            onClick={() => { setCompleted(false); setActiveTopic(null); window.__trainingOpenPicker?.(); }}
            style={{ border: "0.5px solid #ddd", background: "#fff", borderRadius: 8, padding: "7px 12px", fontSize: 12, cursor: "pointer" }}
          >
            Try another topic →
          </button>
          <button
            type="button"
            data-testid="training-complete-exit"
            onClick={() => window.__trainingExit?.()}
            style={{ border: "none", background: "transparent", color: "#888", fontSize: 12, cursor: "pointer" }}
          >
            Exit training
          </button>
        </div>
      </div>
    );
  }

  if (suppressSpotlight && !paused) return null;

  // Wrong route — auto-navigate (effect above) + show an interim card with
  // a "Skip this step" escape hatch in case the target page isn't available
  // for this user (RBAC) or fails to hydrate.
  if (activeTopic && currentStep && !routeMatches) {
    return (
      <div
        data-testid="training-wrong-route"
        style={{
          position: "fixed",
          bottom: 80,
          right: 340,
          width: 280,
          background: "#fff",
          borderRadius: 10,
          boxShadow: "0 8px 24px rgba(0,0,0,0.16)",
          border: "0.5px solid #ddd",
          padding: 12,
          zIndex: 2147483647,
        }}
      >
        <div style={{ fontSize: 12, color: "#111", fontWeight: 500, marginBottom: 4 }}>
          Opening {expectedRoute}…
        </div>
        <div style={{ fontSize: 11, color: "#888", marginBottom: 8 }}>
          {currentStep.instruction}
        </div>
        <div style={{ display: "flex", gap: 6, justifyContent: "space-between" }}>
          <button
            type="button"
            data-testid="training-goto-route"
            onClick={() => navigate(expectedRoute)}
            style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 6, padding: "5px 12px", fontSize: 11, fontWeight: 500, cursor: "pointer" }}
          >
            Go now →
          </button>
          <button
            type="button"
            data-testid="training-route-skip"
            onClick={skip}
            style={{ background: "transparent", border: "none", color: "#888", fontSize: 11, cursor: "pointer" }}
          >
            Skip step
          </button>
        </div>
      </div>
    );
  }

  // Paused panel (after pause-at-my-action click)
  if (paused && !completed) {
    return (
      <div
        data-testid="training-paused-card"
        style={{
          position: "fixed",
          bottom: 80, right: 340, width: 280,
          background: "#fff", borderRadius: 10,
          boxShadow: "0 8px 24px rgba(0,0,0,0.16)",
          border: "0.5px solid #ddd", padding: 12,
          zIndex: 2147483647,
        }}
      >
        <div style={{ fontSize: 12, color: "#111", fontWeight: 500, marginBottom: 4 }}>
          Paused — nice work.
        </div>
        <div style={{ fontSize: 11, color: "#888", marginBottom: 8 }}>
          Explore freely. When ready, resume the walkthrough.
        </div>
        <button
          type="button"
          data-testid="training-resume"
          onClick={handleResume}
          style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 6, padding: "5px 12px", fontSize: 11, fontWeight: 500, cursor: "pointer" }}
        >
          Resume →
        </button>
      </div>
    );
  }

  // Normal step spotlight
  return (
    <>
      <Spotlight
        step={stepIndex + 1}
        total={total}
        instruction={currentStep.instruction}
        selector={currentStep.spotlight}
        canBack={stepIndex > 0}
        isLastStep={stepIndex + 1 >= total}
        pauseAtAction={pauseAtAction}
        onNext={advance}
        onBack={back}
        onSkip={skip}
        onFinish={finish}
        onPausedAction={handlePausedAction}
      />
      {/* pause-mode toggle hint (inline, mirrors §3 controls + enhancement) */}
      <div
        data-testid="training-pause-mode-row"
        style={{
          position: "fixed",
          bottom: 20,
          left: 20,
          background: "#0F2942",
          color: "#fff",
          borderRadius: 20,
          padding: "6px 10px",
          fontSize: 11,
          zIndex: 2147483647,
          display: "flex", alignItems: "center", gap: 8,
          boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
        }}
      >
        <span>Auto-advance</span>
        <button
          type="button"
          onClick={togglePauseMode}
          data-testid="training-pause-mode-toggle"
          aria-pressed={pauseAtAction}
          style={{
            width: 36, height: 18, borderRadius: 9,
            background: pauseAtAction ? "#2563eb" : "#566B85",
            border: "none", cursor: "pointer",
            position: "relative", padding: 0,
          }}
        >
          <span style={{
            width: 14, height: 14, borderRadius: "50%",
            background: "#fff", position: "absolute", top: 2,
            left: pauseAtAction ? 20 : 2,
            transition: "left 0.2s ease",
          }} />
        </button>
        <span>Pause at my action</span>
        <button
          type="button"
          data-testid="training-change-topic"
          onClick={handleChangeTopic}
          style={{ background: "transparent", border: "0.5px solid rgba(255,255,255,0.25)", color: "#8BA5C0", borderRadius: 6, padding: "2px 8px", fontSize: 10, cursor: "pointer", marginLeft: 6 }}
        >
          Change topic
        </button>
      </div>
    </>
  );
}
